import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Eye,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Columns,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ListLoader } from '@/components/shared/ListLoader'
import type { InventoryAdjustmentRow } from '../types'
import { useAuthStore } from '@/store/auth.store'
import { inventoryAdjustmentService } from '@/services/inventoryAdjustmentService'

const PAGE_SIZE = 10

type ColumnKey = 'code' | 'reference' | 'warehouse'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'code', labelKey: 'inventoryAdjustments.columns.code' },
  { key: 'reference', labelKey: 'inventoryAdjustments.columns.reference' },
  { key: 'warehouse', labelKey: 'inventoryAdjustments.columns.warehouse' },
]

export const InventoryAdjustmentList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)

  const [rows, setRows] = useState<InventoryAdjustmentRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
    code: true,
    reference: true,
    warehouse: true,
  })

  const prevBranchIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (!selectedBranchId) {
      setRows([])
      setTotalCount(0)
      prevBranchIdRef.current = null
      return
    }
    const prev = prevBranchIdRef.current
    if (prev !== null && prev !== selectedBranchId) {
      prevBranchIdRef.current = selectedBranchId
      setPageNumber(1)
      return
    }
    prevBranchIdRef.current = selectedBranchId

    let cancelled = false
    setLoading(true)
    void inventoryAdjustmentService
      .getAdjustments(selectedBranchId, pageNumber, PAGE_SIZE)
      .then((res) => {
        if (!cancelled) {
          setRows(res.items)
          setTotalCount(res.totalCount)
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setRows([])
          setTotalCount(0)
          const msg =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
              : undefined
          toast.error(msg || t('inventoryAdjustments.listLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedBranchId, pageNumber, t])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.code, r.reference, r.warehouse].some((field) =>
        String(field).toLowerCase().includes(q)
      )
    )
  }, [rows, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const page = Math.min(pageNumber, totalPages)

  const handleAdd = () => navigate('/inventory/adjustments/new')

  const handleView = (row: InventoryAdjustmentRow) => {
    navigate(`/inventory/adjustments/${row.id}/view`)
  }

  const handleCopy = () => {
    if (!selectedBranchId || filtered.length === 0) {
      toast.error(t('inventoryAdjustments.nothingToCopy'))
      return
    }
    const header = ['Code', 'Reference', 'Warehouse']
    const lines = filtered.map((r) => [r.code, r.reference, r.warehouse].join('\t'))
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('inventoryAdjustments.copied'))
  }

  const handleExportCSV = () => {
    if (!selectedBranchId || filtered.length === 0) {
      toast.error(t('inventoryAdjustments.nothingToExport'))
      return
    }
    const header = 'Code,Reference,Warehouse'
    const body = filtered
      .map((r) =>
        [r.code, r.reference, r.warehouse]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `inventory_adjustments_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('inventoryAdjustments.exported'))
  }

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const startIdx = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(page * PAGE_SIZE, totalCount)
  const visibleCount = Object.values(visibleColumns).filter(Boolean).length

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryAdjustments.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryAdjustments.listSubtitle')}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('inventoryAdjustments.addAdjustment')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">{t('inventoryAdjustments.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('inventoryAdjustments.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns className="mr-2 h-4 w-4" />
                    {t('inventoryAdjustments.columnVisibility')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{t('inventoryAdjustments.columnsToggle')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ALL_COLUMNS.map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={visibleColumns[col.key]}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {t(col.labelKey)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('inventoryAdjustments.copy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {visibleColumns.code && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryAdjustments.columns.code')}
                    </TableHead>
                  )}
                  {visibleColumns.reference && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryAdjustments.columns.reference')}
                    </TableHead>
                  )}
                  {visibleColumns.warehouse && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryAdjustments.columns.warehouse')}
                    </TableHead>
                  )}
                  <TableHead className="h-12 w-[88px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('inventoryAdjustments.columns.view')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleCount + 1} className="p-0">
                      <ListLoader message={t('common.loading', { defaultValue: 'Loading...' })} />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleCount + 1}
                      className="py-12 text-center text-muted-foreground"
                    >
                      {!selectedBranchId
                        ? t('unsellableInventory.selectBranchForWarehouses')
                        : t('inventoryAdjustments.emptyTable')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => (
                    <TableRow
                      key={row.id}
                      className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10"
                    >
                      {visibleColumns.code && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.code}</TableCell>
                      )}
                      {visibleColumns.reference && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.reference}</TableCell>
                      )}
                      {visibleColumns.warehouse && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.warehouse}</TableCell>
                      )}
                      <TableCell className="px-4 py-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleView(row)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              {t('inventoryAdjustments.showing', { start: startIdx, end: endIdx, total: totalCount })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">{page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
